import { useQuery } from '@tanstack/react-query';
import { formatMoney } from '@vns/core';
import { supabase } from './supabase';
import type { ExportColumn } from './export';
import type { IconName } from '../components/ui';

function unwrap<T>({ data, error }: { data: T; error: { message: string } | null }): NonNullable<T> {
  if (error) throw new Error(error.message);
  if (data === null || data === undefined) throw new Error('No data returned');
  return data as NonNullable<T>;
}

const rel = (v: unknown, k = 'name') => (v as Record<string, string> | null)?.[k] ?? '—';
const day = (iso: unknown) =>
  iso ? new Date(`${String(iso)}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const num = (n: unknown) => Number(n).toLocaleString('en-IN', { maximumFractionDigits: 3 });

type Row = Record<string, unknown>;

export interface MaterialPickerRow {
  id: string;
  name: string;
  brand: string | null;
  unitName: string | null;
  ratePaise: number;
  supplierId: string | null;
  supplierName: string | null;
}

export function useMaterialPickerList() {
  return useQuery({
    queryKey: ['material-report-picker'],
    queryFn: async (): Promise<MaterialPickerRow[]> => {
      const rows = unwrap(
        await supabase
          .from('materials')
          .select('id, name, brand, rate_paise, supplier_id, units(short_name), suppliers(name)')
          .is('deleted_at', null)
          .order('name'),
      );
      return rows.map((r) => ({
        id: r.id,
        name: r.name,
        brand: r.brand,
        unitName: rel(r.units, 'short_name'),
        ratePaise: r.rate_paise,
        supplierId: r.supplier_id,
        supplierName: rel(r.suppliers),
      }));
    },
  });
}

export interface MaterialReportSection {
  key: string;
  title: string;
  icon: IconName;
  columns: ExportColumn[];
  rows: Row[];
}

export interface SingleMaterialReportData {
  id: string;
  name: string;
  brand: string | null;
  unitName: string;
  ratePaise: number;
  supplierName: string;
  openingQty: number;
  totalPurchasedQty: number;
  totalPurchasedAmountPaise: number;
  totalUsedQty: number;
  stockOnHand: number;
  purchases: Row[];
  usage: Row[];
  transfers: Row[];
}

export function useMaterialFullReportData(materialId: string) {
  return useQuery({
    queryKey: ['material-full-report', materialId],
    enabled: !!materialId,
    queryFn: async (): Promise<SingleMaterialReportData> => {
      const material = unwrap(
        await supabase
          .from('materials')
          .select('id, name, brand, rate_paise, opening_qty, units(short_name), suppliers(name)')
          .eq('id', materialId)
          .is('deleted_at', null)
          .single(),
      );

      const [purchaseRows, usageRows, transferRows] = await Promise.all([
        unwrap(
          await supabase
            .from('material_purchases')
            .select('id, purchased_on, quantity, rate_paise, amount_paise, invoice_no, advance_paid_paise, project_id, projects(id, name), suppliers(id, name), units(short_name)')
            .eq('material_id', materialId)
            .eq('approval_status', 'approved')
            .is('deleted_at', null)
            .order('purchased_on', { ascending: false }),
        ),
        unwrap(
          await supabase
            .from('material_usage')
            .select('id, used_on, quantity, remarks, project_id, projects(id, name), units(short_name)')
            .eq('material_id', materialId)
            .eq('approval_status', 'approved')
            .is('deleted_at', null)
            .order('used_on', { ascending: false }),
        ),
        unwrap(
          await supabase
            .from('stock_transfers')
            .select('id, transferred_on, quantity, rate_paise, amount_paise, vehicle_no, driver_name, remarks, from_project_id, to_project_id, units(short_name), from_project:projects!stock_transfers_from_project_id_fkey(id, name), to_project:projects!stock_transfers_to_project_id_fkey(id, name)')
            .eq('material_id', materialId)
            .eq('approval_status', 'approved')
            .is('deleted_at', null)
            .order('transferred_on', { ascending: false }),
        ),
      ]);

      const totalPurchasedQty = purchaseRows.reduce((s, p) => s + Number(p.quantity), 0);
      const totalPurchasedAmountPaise = purchaseRows.reduce((s, p) => s + Number(p.amount_paise), 0);
      const totalUsedQty = usageRows.reduce((s, u) => s + Number(u.quantity), 0);
      const openingQty = Number(material.opening_qty);
      const stockOnHand = openingQty + totalPurchasedQty - totalUsedQty;

      return {
        id: material.id,
        name: material.name,
        brand: material.brand,
        unitName: rel(material.units, 'short_name'),
        ratePaise: material.rate_paise,
        supplierName: rel(material.suppliers),
        openingQty,
        totalPurchasedQty,
        totalPurchasedAmountPaise,
        totalUsedQty,
        stockOnHand,
        purchases: purchaseRows,
        usage: usageRows,
        transfers: transferRows,
      };
    },
  });
}

export interface AllMaterialsReportData {
  materials: Array<{ id: string; name: string; brand: string | null; rate_paise: number; opening_qty: number; units: { short_name: string } | null; suppliers: { name: string } | null }>;
  purchases: Row[];
  usage: Row[];
  transfers: Row[];
}

export function useAllMaterialsReportData(enabled: boolean) {
  return useQuery({
    queryKey: ['all-materials-report'],
    enabled,
    queryFn: async (): Promise<AllMaterialsReportData> => {
      const [materials, purchases, usage, transfers] = await Promise.all([
        unwrap(
          await supabase
            .from('materials')
            .select('id, name, brand, rate_paise, opening_qty, units(short_name), suppliers(name)')
            .is('deleted_at', null)
            .order('name'),
        ),
        unwrap(
          await supabase
            .from('material_purchases')
            .select('id, material_id, purchased_on, quantity, rate_paise, amount_paise, invoice_no, advance_paid_paise, project_id, projects(id, name), materials(id, name), suppliers(id, name), units(short_name)')
            .eq('approval_status', 'approved')
            .is('deleted_at', null)
            .order('purchased_on', { ascending: false }),
        ),
        unwrap(
          await supabase
            .from('material_usage')
            .select('id, material_id, used_on, quantity, remarks, project_id, projects(id, name), materials(id, name), units(short_name)')
            .eq('approval_status', 'approved')
            .is('deleted_at', null)
            .order('used_on', { ascending: false }),
        ),
        unwrap(
          await supabase
            .from('stock_transfers')
            .select('id, material_id, transferred_on, quantity, rate_paise, amount_paise, vehicle_no, driver_name, remarks, from_project_id, to_project_id, materials(id, name), units(short_name), from_project:projects!stock_transfers_from_project_id_fkey(id, name), to_project:projects!stock_transfers_to_project_id_fkey(id, name)')
            .eq('approval_status', 'approved')
            .is('deleted_at', null)
            .order('transferred_on', { ascending: false }),
        ),
      ]);

      return {
        materials,
        purchases,
        usage,
        transfers,
      };
    },
  });
}

/* ------------------------------- Single Material Section Builders ------------------------------- */

export function materialPurchasesSection(rows: Row[], unitName: string): MaterialReportSection {
  return {
    key: 'purchases',
    title: 'Purchases & Billing History',
    icon: 'truck',
    columns: [
      { key: 'sno', label: 'S.No', numeric: true },
      { key: 'date', label: 'Date' },
      { key: 'project', label: 'Project' },
      { key: 'supplier', label: 'Supplier' },
      { key: 'invoice', label: 'Invoice No.' },
      { key: 'quantity', label: 'Quantity', numeric: true },
      { key: 'unit', label: 'Unit' },
      { key: 'rate', label: 'Rate', numeric: true, money: true },
      { key: 'amount', label: 'Total Amount', numeric: true, money: true },
      { key: 'paid', label: 'Advance Paid', numeric: true, money: true },
      { key: 'balance', label: 'Balance Due', numeric: true, money: true },
    ],
    rows: rows
      .map((r) => {
        const qtyVal = Number(r.quantity);
        const amount = Number(r.amount_paise);
        const advance = Number(r.advance_paid_paise ?? 0);
        return {
          date: day(r.purchased_on),
          _sort: String(r.purchased_on ?? ''),
          project: rel(r.projects),
          supplier: rel(r.suppliers),
          invoice: (r.invoice_no as string) || '—',
          quantity: num(qtyVal),
          _quantity: qtyVal,
          unit: unitName,
          rate: formatMoney(Number(r.rate_paise)),
          amount: formatMoney(amount),
          _amount: amount,
          paid: formatMoney(advance),
          _paid: advance,
          balance: formatMoney(Math.max(0, amount - advance)),
          _balance: Math.max(0, amount - advance),
        };
      })
      .sort((a, b) => b._sort.localeCompare(a._sort))
      .map((r, i) => ({ sno: i + 1, ...r })),
  };
}

export function materialUsageSection(rows: Row[], unitName: string, openingQty: number): MaterialReportSection {
  type Ev = { date: string; delta: number; usageId?: string };
  const events: Ev[] = rows
    .map((r) => ({ date: String(r.used_on ?? ''), delta: -Number(r.quantity), usageId: String(r.id) }))
    .sort((a, b) => a.date.localeCompare(b.date));

  let run = openingQty;
  const balMap = new Map<string, number>();
  for (const e of events) {
    run += e.delta;
    if (e.usageId) balMap.set(e.usageId, run);
  }

  return {
    key: 'usage',
    title: 'Site Usage History',
    icon: 'box',
    columns: [
      { key: 'sno', label: 'S.No', numeric: true },
      { key: 'date', label: 'Date' },
      { key: 'project', label: 'Project' },
      { key: 'quantity', label: 'Quantity Used', numeric: true },
      { key: 'unit', label: 'Unit' },
      { key: 'balance', label: 'Stock Balance', numeric: true },
      { key: 'remarks', label: 'Remarks / Notes' },
    ],
    rows: rows
      .map((r) => {
        const qtyVal = Number(r.quantity);
        return {
          date: day(r.used_on),
          _sort: String(r.used_on ?? ''),
          project: rel(r.projects),
          quantity: num(qtyVal),
          _quantity: qtyVal,
          unit: unitName,
          balance: `${num(balMap.get(String(r.id)) ?? 0)} ${unitName}`,
          remarks: (r.remarks as string) || '—',
        };
      })
      .sort((a, b) => b._sort.localeCompare(a._sort))
      .map((r, i) => ({ sno: i + 1, ...r })),
  };
}

export function materialTransfersSection(rows: Row[], unitName: string): MaterialReportSection {
  return {
    key: 'transfers',
    title: 'Inter-Project Stock Transfers',
    icon: 'repeat',
    columns: [
      { key: 'sno', label: 'S.No', numeric: true },
      { key: 'date', label: 'Date' },
      { key: 'fromProject', label: 'From Project' },
      { key: 'toProject', label: 'To Project' },
      { key: 'quantity', label: 'Quantity', numeric: true },
      { key: 'unit', label: 'Unit' },
      { key: 'rate', label: 'Rate', numeric: true, money: true },
      { key: 'amount', label: 'Total Value', numeric: true, money: true },
      { key: 'vehicle', label: 'Vehicle No.' },
      { key: 'driver', label: 'Driver' },
      { key: 'remarks', label: 'Remarks' },
    ],
    rows: rows
      .map((r) => {
        const qtyVal = Number(r.quantity);
        const ratePaise = r.rate_paise != null ? Number(r.rate_paise) : null;
        const amountPaise = r.amount_paise != null ? Number(r.amount_paise) : null;
        return {
          date: day(r.transferred_on),
          _sort: String(r.transferred_on ?? ''),
          fromProject: rel(r.from_project),
          toProject: rel(r.to_project),
          quantity: num(qtyVal),
          _quantity: qtyVal,
          unit: unitName,
          rate: ratePaise != null ? formatMoney(ratePaise) : '—',
          amount: amountPaise != null ? formatMoney(amountPaise) : '—',
          _amount: amountPaise ?? 0,
          vehicle: (r.vehicle_no as string) || '—',
          driver: (r.driver_name as string) || '—',
          remarks: (r.remarks as string) || '—',
        };
      })
      .sort((a, b) => b._sort.localeCompare(a._sort))
      .map((r, i) => ({ sno: i + 1, ...r })),
  };
}

export function materialProjectDistributionSection(
  purchases: Row[],
  usage: Row[],
  transfers: Row[],
  unitName: string,
  ratePaise: number,
): MaterialReportSection {
  interface ProjStat {
    projectName: string;
    purchasedQty: number;
    purchasedAmount: number;
    usedQty: number;
    transferredIn: number;
    transferredOut: number;
  }

  const map = new Map<string, ProjStat>();
  const get = (id: string, name: string) => {
    if (!map.has(id)) {
      map.set(id, { projectName: name, purchasedQty: 0, purchasedAmount: 0, usedQty: 0, transferredIn: 0, transferredOut: 0 });
    }
    return map.get(id)!;
  };

  for (const p of purchases) {
    const pid = String(p.project_id || '');
    if (pid) {
      const s = get(pid, rel(p.projects));
      s.purchasedQty += Number(p.quantity);
      s.purchasedAmount += Number(p.amount_paise);
    }
  }

  for (const u of usage) {
    const pid = String(u.project_id || '');
    if (pid) {
      const s = get(pid, rel(u.projects));
      s.usedQty += Number(u.quantity);
    }
  }

  for (const t of transfers) {
    const fid = String(t.from_project_id || '');
    const tid = String(t.to_project_id || '');
    if (fid) {
      const s = get(fid, rel(t.from_project));
      s.transferredOut += Number(t.quantity);
    }
    if (tid) {
      const s = get(tid, rel(t.to_project));
      s.transferredIn += Number(t.quantity);
    }
  }

  const todayStr = day(new Date());
  const rows = Array.from(map.values())
    .map((s, idx) => {
      const netTransfers = s.transferredIn - s.transferredOut;
      const currentStock = s.purchasedQty + netTransfers - s.usedQty;
      const stockVal = Math.round(Math.max(0, currentStock) * ratePaise);
      return {
        sno: idx + 1,
        date: todayStr,
        project: s.projectName,
        purchased: `${num(s.purchasedQty)} ${unitName}`,
        _purchased: s.purchasedQty,
        used: `${num(s.usedQty)} ${unitName}`,
        _used: s.usedQty,
        transfers: netTransfers >= 0 ? `+${num(netTransfers)}` : num(netTransfers),
        _transfers: netTransfers,
        available: `${num(currentStock)} ${unitName}`,
        _available: currentStock,
        stockValue: formatMoney(stockVal),
        _stockValue: stockVal,
      };
    })
    .sort((a, b) => b._purchased - a._purchased)
    .map((r, i) => ({ ...r, sno: i + 1 }));

  return {
    key: 'projectDistribution',
    title: 'Project-wise Consumption & Stock Position',
    icon: 'briefcase',
    columns: [
      { key: 'sno', label: 'S.No', numeric: true },
      { key: 'date', label: 'Date' },
      { key: 'project', label: 'Project' },
      { key: 'purchased', label: 'Purchased Qty', numeric: true },
      { key: 'used', label: 'Used Qty', numeric: true },
      { key: 'transfers', label: 'Net Transfers', numeric: true },
      { key: 'available', label: 'Site Stock', numeric: true },
      { key: 'stockValue', label: 'Site Stock Value', numeric: true, money: true },
    ],
    rows,
  };
}

export function materialSupplierSummarySection(purchases: Row[], unitName: string): MaterialReportSection {
  const map = new Map<string, { supplierName: string; count: number; qty: number; amount: number; paid: number }>();
  for (const p of purchases) {
    const name = rel(p.suppliers);
    const cur = map.get(name) ?? { supplierName: name, count: 0, qty: 0, amount: 0, paid: 0 };
    cur.count += 1;
    cur.qty += Number(p.quantity);
    cur.amount += Number(p.amount_paise);
    cur.paid += Number(p.advance_paid_paise ?? 0);
    map.set(name, cur);
  }

  const todayStr = day(new Date());
  const rows = Array.from(map.values())
    .map((s, idx) => ({
      sno: idx + 1,
      date: todayStr,
      supplier: s.supplierName,
      orders: s.count,
      _orders: s.count,
      quantity: `${num(s.qty)} ${unitName}`,
      _quantity: s.qty,
      amount: formatMoney(s.amount),
      _amount: s.amount,
      paid: formatMoney(s.paid),
      _paid: s.paid,
      balance: formatMoney(Math.max(0, s.amount - s.paid)),
      _balance: Math.max(0, s.amount - s.paid),
    }))
    .sort((a, b) => b._amount - a._amount)
    .map((r, i) => ({ ...r, sno: i + 1 }));

  return {
    key: 'suppliers',
    title: 'Supplier Summary',
    icon: 'truck',
    columns: [
      { key: 'sno', label: 'S.No', numeric: true },
      { key: 'date', label: 'Date' },
      { key: 'supplier', label: 'Supplier' },
      { key: 'orders', label: 'Orders / Invoices', numeric: true },
      { key: 'quantity', label: 'Quantity Supplied', numeric: true },
      { key: 'amount', label: 'Total Purchase Value', numeric: true, money: true },
      { key: 'paid', label: 'Advance Paid', numeric: true, money: true },
      { key: 'balance', label: 'Outstanding Balance', numeric: true, money: true },
    ],
    rows,
  };
}

export function materialFinancialSummarySection(
  material: { ratePaise: number; stockOnHand: number; totalPurchasedQty: number; totalUsedQty: number },
  purchases: Row[],
): MaterialReportSection {
  const totalPurchaseValue = purchases.reduce((s, p) => s + Number(p.amount_paise), 0);
  const totalAdvancePaid = purchases.reduce((s, p) => s + Number(p.advance_paid_paise ?? 0), 0);
  const totalOutstanding = Math.max(0, totalPurchaseValue - totalAdvancePaid);
  const avgPurchaseRate = material.totalPurchasedQty ? Math.round(totalPurchaseValue / material.totalPurchasedQty) : material.ratePaise;
  const stockOnHandValue = Math.round(Math.max(0, material.stockOnHand) * avgPurchaseRate);
  const stockConsumedValue = Math.round(material.totalUsedQty * avgPurchaseRate);

  const todayStr = day(new Date());
  let idx = 0;
  const line = (item: string, amount: number) => {
    idx += 1;
    return { sno: idx, date: todayStr, item, amount: formatMoney(amount), _amount: amount };
  };

  return {
    key: 'financial',
    title: 'Financial & Valuation Summary',
    icon: 'wallet',
    columns: [
      { key: 'sno', label: 'S.No', numeric: true },
      { key: 'date', label: 'Date' },
      { key: 'item', label: 'Item' },
      { key: 'amount', label: 'Amount', numeric: true, money: true },
    ],
    rows: [
      line('Total Purchases (Billed Amount)', totalPurchaseValue),
      line('Advance Paid to Suppliers', totalAdvancePaid),
      line('Outstanding Payable to Suppliers', totalOutstanding),
      line('Estimated Value of Material Consumed', stockConsumedValue),
      line('Current Valuation of Stock on Hand', stockOnHandValue),
    ],
  };
}

/* ------------------------------- All Materials (Multi) Section Builders ------------------------------- */

export function allMaterialsStockSummarySection(
  materials: AllMaterialsReportData['materials'],
  purchases: Row[],
  usage: Row[],
  transfers: Row[],
): MaterialReportSection {
  const boughtMap = new Map<string, { qty: number; amount: number }>();
  for (const p of purchases) {
    const mid = String(p.material_id || '');
    const cur = boughtMap.get(mid) ?? { qty: 0, amount: 0 };
    cur.qty += Number(p.quantity);
    cur.amount += Number(p.amount_paise);
    boughtMap.set(mid, cur);
  }

  const usedMap = new Map<string, number>();
  for (const u of usage) {
    const mid = String(u.material_id || '');
    usedMap.set(mid, (usedMap.get(mid) ?? 0) + Number(u.quantity));
  }

  const transMap = new Map<string, number>();
  for (const t of transfers) {
    const mid = String(t.material_id || '');
    transMap.set(mid, (transMap.get(mid) ?? 0) + Number(t.quantity));
  }

  const todayStr = day(new Date());
  const rows = materials
    .map((m, idx) => {
      const b = boughtMap.get(m.id) ?? { qty: 0, amount: 0 };
      const u = usedMap.get(m.id) ?? 0;
      const t = transMap.get(m.id) ?? 0;
      const open = Number(m.opening_qty ?? 0);
      const available = open + b.qty - u;
      const rate = m.rate_paise;
      const val = Math.round(Math.max(0, available) * rate);
      const unit = m.units?.short_name ?? '';
      return {
        sno: idx + 1,
        date: todayStr,
        material: m.name,
        brand: m.brand || '—',
        unit,
        rate: formatMoney(rate),
        purchased: `${num(b.qty)} ${unit}`,
        _purchased: b.qty,
        purchaseAmount: formatMoney(b.amount),
        _purchaseAmount: b.amount,
        used: `${num(u)} ${unit}`,
        _used: u,
        available: `${num(available)} ${unit}`,
        _available: available,
        stockValue: formatMoney(val),
        _stockValue: val,
        _any: b.qty + u + t + open,
      };
    })
    .filter((r) => Number(r._any) > 0)
    .sort((a, b) => b._stockValue - a._stockValue)
    .map((r, i) => ({ ...r, sno: i + 1 }));

  return {
    key: 'materialStockSummary',
    title: 'Materials Inventory & Stock Summary',
    icon: 'box',
    columns: [
      { key: 'sno', label: 'S.No', numeric: true },
      { key: 'date', label: 'Date' },
      { key: 'material', label: 'Material' },
      { key: 'brand', label: 'Brand' },
      { key: 'unit', label: 'Unit' },
      { key: 'rate', label: 'Rate', numeric: true, money: true },
      { key: 'purchased', label: 'Purchased Qty', numeric: true },
      { key: 'purchaseAmount', label: 'Purchased Value', numeric: true, money: true },
      { key: 'used', label: 'Used Qty', numeric: true },
      { key: 'available', label: 'Stock on Hand', numeric: true },
      { key: 'stockValue', label: 'Stock Valuation', numeric: true, money: true },
    ],
    rows,
  };
}

export function allMaterialsPurchasesSection(rows: Row[]): MaterialReportSection {
  return {
    key: 'allPurchases',
    title: 'Material Purchases & Billing Orders',
    icon: 'truck',
    columns: [
      { key: 'sno', label: 'S.No', numeric: true },
      { key: 'date', label: 'Date' },
      { key: 'material', label: 'Material' },
      { key: 'project', label: 'Project' },
      { key: 'supplier', label: 'Supplier' },
      { key: 'invoice', label: 'Invoice No.' },
      { key: 'quantity', label: 'Qty', numeric: true },
      { key: 'unit', label: 'Unit' },
      { key: 'rate', label: 'Rate', numeric: true, money: true },
      { key: 'amount', label: 'Total Amount', numeric: true, money: true },
      { key: 'paid', label: 'Advance Paid', numeric: true, money: true },
      { key: 'balance', label: 'Balance Due', numeric: true, money: true },
    ],
    rows: rows
      .map((r) => {
        const qtyVal = Number(r.quantity);
        const amount = Number(r.amount_paise);
        const advance = Number(r.advance_paid_paise ?? 0);
        return {
          date: day(r.purchased_on),
          _sort: String(r.purchased_on ?? ''),
          material: rel(r.materials),
          project: rel(r.projects),
          supplier: rel(r.suppliers),
          invoice: (r.invoice_no as string) || '—',
          quantity: num(qtyVal),
          _quantity: qtyVal,
          unit: rel(r.units, 'short_name'),
          rate: formatMoney(Number(r.rate_paise)),
          amount: formatMoney(amount),
          _amount: amount,
          paid: formatMoney(advance),
          _paid: advance,
          balance: formatMoney(Math.max(0, amount - advance)),
          _balance: Math.max(0, amount - advance),
        };
      })
      .sort((a, b) => b._sort.localeCompare(a._sort))
      .map((r, i) => ({ sno: i + 1, ...r })),
  };
}

export function allMaterialsUsageSection(rows: Row[]): MaterialReportSection {
  return {
    key: 'allUsage',
    title: 'Site Material Usage Log',
    icon: 'box',
    columns: [
      { key: 'sno', label: 'S.No', numeric: true },
      { key: 'date', label: 'Date' },
      { key: 'material', label: 'Material' },
      { key: 'project', label: 'Project' },
      { key: 'quantity', label: 'Quantity Used', numeric: true },
      { key: 'unit', label: 'Unit' },
      { key: 'remarks', label: 'Remarks / Notes' },
    ],
    rows: rows
      .map((r) => {
        const qtyVal = Number(r.quantity);
        return {
          date: day(r.used_on),
          _sort: String(r.used_on ?? ''),
          material: rel(r.materials),
          project: rel(r.projects),
          quantity: num(qtyVal),
          _quantity: qtyVal,
          unit: rel(r.units, 'short_name'),
          remarks: (r.remarks as string) || '—',
        };
      })
      .sort((a, b) => b._sort.localeCompare(a._sort))
      .map((r, i) => ({ sno: i + 1, ...r })),
  };
}

export function allMaterialsTransfersSection(rows: Row[]): MaterialReportSection {
  return {
    key: 'allTransfers',
    title: 'Inter-Project Stock Transfers',
    icon: 'repeat',
    columns: [
      { key: 'sno', label: 'S.No', numeric: true },
      { key: 'date', label: 'Date' },
      { key: 'material', label: 'Material' },
      { key: 'fromProject', label: 'From Project' },
      { key: 'toProject', label: 'To Project' },
      { key: 'quantity', label: 'Quantity', numeric: true },
      { key: 'unit', label: 'Unit' },
      { key: 'rate', label: 'Rate', numeric: true, money: true },
      { key: 'amount', label: 'Total Value', numeric: true, money: true },
      { key: 'vehicle', label: 'Vehicle No.' },
      { key: 'driver', label: 'Driver' },
      { key: 'remarks', label: 'Remarks' },
    ],
    rows: rows
      .map((r) => {
        const qtyVal = Number(r.quantity);
        const ratePaise = r.rate_paise != null ? Number(r.rate_paise) : null;
        const amountPaise = r.amount_paise != null ? Number(r.amount_paise) : null;
        return {
          date: day(r.transferred_on),
          _sort: String(r.transferred_on ?? ''),
          material: rel(r.materials),
          fromProject: rel(r.from_project),
          toProject: rel(r.to_project),
          quantity: num(qtyVal),
          _quantity: qtyVal,
          unit: rel(r.units, 'short_name'),
          rate: ratePaise != null ? formatMoney(ratePaise) : '—',
          amount: amountPaise != null ? formatMoney(amountPaise) : '—',
          _amount: amountPaise ?? 0,
          vehicle: (r.vehicle_no as string) || '—',
          driver: (r.driver_name as string) || '—',
          remarks: (r.remarks as string) || '—',
        };
      })
      .sort((a, b) => b._sort.localeCompare(a._sort))
      .map((r, i) => ({ sno: i + 1, ...r })),
  };
}

export function allMaterialsSupplierSection(purchases: Row[]): MaterialReportSection {
  const map = new Map<string, { supplierName: string; count: number; materials: Set<string>; amount: number; paid: number }>();
  for (const p of purchases) {
    const name = rel(p.suppliers);
    const cur = map.get(name) ?? { supplierName: name, count: 0, materials: new Set<string>(), amount: 0, paid: 0 };
    cur.count += 1;
    cur.materials.add(rel(p.materials));
    cur.amount += Number(p.amount_paise);
    cur.paid += Number(p.advance_paid_paise ?? 0);
    map.set(name, cur);
  }

  const todayStr = day(new Date());
  const rows = Array.from(map.values())
    .map((s, idx) => ({
      sno: idx + 1,
      date: todayStr,
      supplier: s.supplierName,
      orders: s.count,
      _orders: s.count,
      materials: Array.from(s.materials).join(', '),
      amount: formatMoney(s.amount),
      _amount: s.amount,
      paid: formatMoney(s.paid),
      _paid: s.paid,
      balance: formatMoney(Math.max(0, s.amount - s.paid)),
      _balance: Math.max(0, s.amount - s.paid),
    }))
    .sort((a, b) => b._amount - a._amount)
    .map((r, i) => ({ ...r, sno: i + 1 }));

  return {
    key: 'suppliers',
    title: 'Supplier Purchase & Balance Summary',
    icon: 'truck',
    columns: [
      { key: 'sno', label: 'S.No', numeric: true },
      { key: 'date', label: 'Date' },
      { key: 'supplier', label: 'Supplier' },
      { key: 'orders', label: 'Orders / Invoices', numeric: true },
      { key: 'materials', label: 'Materials Supplied' },
      { key: 'amount', label: 'Total Purchase Value', numeric: true, money: true },
      { key: 'paid', label: 'Advance Paid', numeric: true, money: true },
      { key: 'balance', label: 'Outstanding Balance', numeric: true, money: true },
    ],
    rows,
  };
}

export function allMaterialsFinancialSection(purchases: Row[], materials: AllMaterialsReportData['materials'], usage: Row[]): MaterialReportSection {
  const totalPurchaseValue = purchases.reduce((s, p) => s + Number(p.amount_paise), 0);
  const totalAdvancePaid = purchases.reduce((s, p) => s + Number(p.advance_paid_paise ?? 0), 0);
  const totalOutstanding = Math.max(0, totalPurchaseValue - totalAdvancePaid);

  const usedMap = new Map<string, number>();
  for (const u of usage) {
    const mid = String(u.material_id || '');
    usedMap.set(mid, (usedMap.get(mid) ?? 0) + Number(u.quantity));
  }

  const boughtMap = new Map<string, number>();
  for (const p of purchases) {
    const mid = String(p.material_id || '');
    boughtMap.set(mid, (boughtMap.get(mid) ?? 0) + Number(p.quantity));
  }

  let totalStockVal = 0;
  let totalUsageVal = 0;
  for (const m of materials) {
    const b = boughtMap.get(m.id) ?? 0;
    const u = usedMap.get(m.id) ?? 0;
    const open = Number(m.opening_qty ?? 0);
    const avail = open + b - u;
    if (avail > 0) totalStockVal += Math.round(avail * m.rate_paise);
    if (u > 0) totalUsageVal += Math.round(u * m.rate_paise);
  }

  const todayStr = day(new Date());
  let idx = 0;
  const line = (item: string, amount: number) => {
    idx += 1;
    return { sno: idx, date: todayStr, item, amount: formatMoney(amount), _amount: amount };
  };

  return {
    key: 'financial',
    title: 'Financial & Inventory Valuation Summary',
    icon: 'wallet',
    columns: [
      { key: 'sno', label: 'S.No', numeric: true },
      { key: 'date', label: 'Date' },
      { key: 'item', label: 'Item' },
      { key: 'amount', label: 'Amount', numeric: true, money: true },
    ],
    rows: [
      line('Total Purchases Across Materials', totalPurchaseValue),
      line('Total Advance Paid to Suppliers', totalAdvancePaid),
      line('Total Outstanding Payable to Suppliers', totalOutstanding),
      line('Estimated Value of Materials Consumed', totalUsageVal),
      line('Current Valuation of Total Stock on Hand', totalStockVal),
    ],
  };
}
